import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import Button from '../Button/Button';
import { request, requestUpload } from '../../services/exportMetRateData';
import { initReplay } from '../../services/replay';
import { downloadFodPlotFromCsvUrl, pickMetRateKey } from '../../services/generateFodPlot';

export default function MetRateDownloadButtons(props) {
  const {
    metRateRequest,
    ecsSuitRequest,
    heartRateRequest,
    humidTempRequest,
    uploadRequest,
    downloadLinks,
    setDownloadLinks,
    location,
    crewMember,
    hasUmbilicalChange,
    metRateSensors,
    heartRateSensors,
    jwt,
    isFod,
  } = props || {};

  const requestDataLabel = crewMember ? `Request Data for ${crewMember}` : 'Request Data';

  let cachedState = {};
  try {
    cachedState = JSON.parse(sessionStorage.getItem('downloadState'));
  } catch (e) {
    // ignore error - assume no state was cached
  }

  const {
    date: mrDate,
    maxTime: mrMaxTime,
    minTime: mrMinTime,
    thingName: mrThingName,
  } = metRateRequest || {};

  const {
    date: ecsDate,
    maxTime: ecsMaxTime,
    minTime: ecsMinTime,
    evNum: ecsThingName, // ECS Primary Key is umbilical_color which comes down in evNum
  } = ecsSuitRequest || {};

  const {
    date: hrDate,
    maxTime: hrMaxTime,
    minTime: hrMinTime,
    thingName: hrThingName,
  } = heartRateRequest || {};

  const {
    date: htDate,
    maxTime: htMaxTime,
    minTime: htMinTime,
    thingName: htThingName,
  } = humidTempRequest || {};

  const { objects: uploadObjects = [] } = uploadRequest || {};

  const {
    requestingData,
    replayingData,
    cancelingDataReplay,
    error,
    message,
  } = downloadLinks || {};

  const [timelineFile, setTimelineFile] = useState('');
  const [timelineFilename, setTimelineFilename] = useState('');
  const [isZipping, setIsZipping] = useState(false);
  const [isFodPlotLoading, setIsFodPlotLoading] = useState(false);
  const [fodPlotError, setFodPlotError] = useState(null);

  useEffect(() => {
    if (requestingData) {
      if (location === 'uploads') {
        requestUpload(uploadObjects)
          .then(setDownloadLinks)
          .catch((errorObject) => setDownloadLinks({ error: errorObject.message }));
      } else {
        request({
          mrDate,
          mrMaxTime,
          mrMinTime,
          mrThingName,
          hrDate,
          hrMaxTime,
          hrMinTime,
          hrThingName,
          htDate,
          htMaxTime,
          htMinTime,
          htThingName,
          ecsDate,
          ecsMaxTime,
          ecsMinTime,
          ecsThingName,
          timelineFile,
          timelineFilename,
        })
          .then(setDownloadLinks)
          .catch((errorObject) => setDownloadLinks({ error: errorObject.message }));
      }
    } else if (replayingData) {
      initReplay({
        mrDate: mrDate || hrDate || htDate,
        mrMinTime,
        hrMinTime,
        metRateSensors,
        heartRateSensors,
        jwt,
      });
      window.location.href = '#/dashboard';
    } else if (cancelingDataReplay) {
      // the reload interrupts the websocket connection; the disconnect cancels data replay
      window.location.reload();
    }
  }, [
    replayingData,
    cancelingDataReplay,
    requestingData,
    ecsDate,
    ecsMaxTime,
    ecsMinTime,
    ecsThingName,
    mrDate,
    mrMaxTime,
    mrMinTime,
    mrThingName,
    hrDate,
    hrMaxTime,
    hrMinTime,
    hrThingName,
    htDate,
    htMaxTime,
    htMinTime,
    htThingName,
    setDownloadLinks,
  ]);

  const specialKeys = ['requestingData', 'replayingData', 'cancelingDataReplay', 'error', 'message', 'warning'];

  const downloadFodPlot = async () => {
    setIsFodPlotLoading(true);
    setFodPlotError(null);
    try {
      const links = await request({
        mrDate,
        mrMaxTime,
        mrMinTime,
        mrThingName,
        hrDate,
        hrMaxTime,
        hrMinTime,
        hrThingName,
        htDate,
        htMaxTime,
        htMinTime,
        htThingName,
        ecsDate,
        ecsMaxTime,
        ecsMinTime,
        ecsThingName,
        timelineFile,
        timelineFilename,
      });

      const metRateKey = pickMetRateKey(links);
      if (!metRateKey || !links[metRateKey]) throw new Error('No met rate data URL returned');

      await downloadFodPlotFromCsvUrl({
        csvUrl: links[metRateKey],
        timelineDataUrl: timelineFile,
        title: `${mrThingName || 'NBL'} | ${mrDate || ''}`,
        filename: `fod-nbl-plot-${mrDate || 'download'}.pdf`,
      });
    } catch (e) {
      setFodPlotError(e.message);
    } finally {
      setIsFodPlotLoading(false);
    }
  };

  const downloadData = () => {
    setDownloadLinks({ requestingData: true });
  };
  const replayData = () => {
    sessionStorage.setItem('downloadState', JSON.stringify({ replayingData: true }));
    setDownloadLinks({ replayingData: true });
  };
  const cancelDataReplay = () => {
    sessionStorage.setItem('downloadState', JSON.stringify({ replayingData: false }));
    setDownloadLinks({ replayingData: false, cancelingDataReplay: true });
  };
  const uploadSmartTimeline = (event) => {
    const reader = new FileReader();
    const { target } = event || {};
    const { files } = target || {};
    const [file] = files || [];
    const { name } = file || {};
    reader.readAsDataURL(file);
    reader.onload = (upload) => {
      setTimelineFile(upload.target.result);
    };
    setTimelineFilename(name);
  };

  const csvLinks = Object.keys(downloadLinks)
    .filter((key) => !specialKeys.includes(key))
    .sort();

  const downloadAllAsZip = async () => {
    setIsZipping(true);
    try {
      const zip = new JSZip();
      const fetches = csvLinks.map(async (key) => {
        const response = await fetch(downloadLinks[key]);
        const blob = await response.blob();
        const filename = key.endsWith('.csv') ? key : `${key}.csv`;
        zip.file(filename, blob);
      });
      await Promise.all(fetches);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sensor-data-${mrDate || hrDate || htDate || ecsDate || 'download'}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadLinks({ ...downloadLinks, error: `Failed to create zip: ${e.message}` });
    } finally {
      setIsZipping(false);
    }
  };

  return (
    <div className="download-actions">
      <label htmlFor="timeline-file" className="upload-smart-timeline">
        <div>
          Upload SmartTimeline
          {' '}
          <span className="upload-smart-timeline-optional">(optional)</span>
        </div>
        <input onChange={uploadSmartTimeline} id="timeline-file" name="file" type="file" />
      </label>
      {hasUmbilicalChange ? (
        <p className="umbilical-change-notice">
          Multiple umbilical runs will be merged into a single file, with the
          aborted run labeled below the rest.
        </p>
      ) : null}
      {isFod ? (
        <>
          <Button
            cName="download-button-primary"
            onClick={downloadFodPlot}
            label={isFodPlotLoading ? 'Generating Plot...' : 'Download FOD Plot'}
            disabled={isFodPlotLoading}
          />
          {fodPlotError ? <span>{fodPlotError}</span> : null}
        </>
      ) : (
        <>
          <Button cName="download-button-primary" onClick={downloadData} label={requestDataLabel} />
          {requestingData ? <span>Requesting...</span> : null}
          {error ? <span>{error}</span> : null}
          {message ? <span>{message}</span> : null}
          {downloadLinks.warning ? <span>{downloadLinks.warning}</span> : null}
          {
            !requestingData && !replayingData && !cancelingDataReplay && !error && !message && csvLinks.length > 0
              ? (
                <Button
                  cName="download-button-primary"
                  onClick={downloadAllAsZip}
                  label={isZipping ? 'Creating Zip...' : `Download All (${csvLinks.length} files) as Zip`}
                  disabled={isZipping}
                />
              )
              : null
          }
        </>
      )}
      {replayingData || (cachedState || {}).replayingData ? (
        <Button cName="download-button-secondary" onClick={cancelDataReplay} label="Cancel Data Replay" />
      ) : (
        <Button cName="download-button-secondary" onClick={replayData} label="Replay Data" />
      )}
    </div>
  );
}
